import React, { ChangeEvent, FC, Fragment } from 'react';
import { Box, Checkbox, Link, Tooltip, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { flatten, isEmpty } from 'lodash';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import moment from 'moment';

import {
  ContentApprovalTypes,
  ContentAudit,
  ContentFragment,
  ContentGroupInput,
  GroupContentApprovalType,
  SearchContentSortFields,
  SortDirections,
} from 'api';
import {
  DataTable,
  DataTableActions,
  DataTableColumn,
  DataTableRowSelection,
  PostImage,
  TextTransform,
} from 'components';
import { CheckedMode, InfiniteQuery, UseCheckedState } from 'types/app';
import { generateLink } from 'Links';
import { getPrimaryGroup, hasVideo, isPhotoOnly, isUrl } from 'features/content/helpers';
import { Body, UrlMetadata } from 'features/activity/components';
import { SortAction, SortParam } from 'components/DataTable';
import { useCurrentUser, useReleases } from 'hooks';

import Actions, { UpdateContentMutationOptions } from './Actions';
import theme from 'theme';
import { SearchContentFilter } from 'features/admin/types';
import { IMAGE_SIZE } from '../../constants';

interface Props extends Partial<UseCheckedState>, InfiniteQuery<ContentFragment> {
  approval: ContentApprovalTypes;
  count: number;
  updateContentMutationOptions: UpdateContentMutationOptions;
  sortBy: SortParam;
  onSort: SortAction;
  filter: SearchContentFilter;
}

const styles = {
  tableBodyRow: {
    height: 136,
  },
  icon: {
    opacity: 0.2,
  },
  checkbox: {
    color: theme.palette.grey[500],
  },
  disabled: {
    color: theme.palette.grey[200],
  },
} as const;

const PostManagementTable: FC<Props> = ({
  approval,
  sortBy,
  onSort,
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  count,
  updateContentMutationOptions,
  filter,
  ...rest
}) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const releases = useReleases();

  const has4EyeModeration = releases.includes('4EyeModeration');
  const { setCheckedState, checkedMode, checkedIds } = rest;
  const isAllChecked = checkedMode !== CheckedMode.OFF;
  const showCheckbox = approval === ContentApprovalTypes.Submitted;
  const actionCount = results.filter(content => {
    const primaryGroup = getPrimaryGroup(content.groups, content.primary_group_id);
    return primaryGroup?.content_approval_type !== GroupContentApprovalType.FourEyes;
  }).length;

  function getType(content: ContentFragment) {
    if (isPhotoOnly(content)) {
      return t('common:image');
    }

    if (hasVideo(content)) {
      return t('common:video');
    }

    if (isUrl(content)) {
      return t('common:link');
    }

    return t('common:Text');
  }

  function getSubmitter(content: ContentFragment) {
    if (content.user) {
      return (
        <Link noWrap component={generateLink(`/profile/${content.user.id}`)} underline="hover">
          {content.user.full_name}
        </Link>
      );
    }

    return t('components:rssFeeds');
  }

  function getSubmitterText(content: ContentFragment) {
    return content.user ? content.user.full_name : t('components:rssFeeds');
  }

  function getGroupTitles(content: ContentFragment) {
    if (!content.groups.length) {
      return '';
    }

    const primaryGroup = getPrimaryGroup(content.groups, content.primary_group_id);

    return (
      <Fragment>
        <Link component={generateLink(`/group/${primaryGroup.id}`)} underline="hover">
          {primaryGroup?.title}
        </Link>
        {content.groups?.length > 1 && ` +${content.groups?.length - 1}`}
      </Fragment>
    );
  }

  function getGroupTitlesText(content: ContentFragment) {
    const primaryGroup = getPrimaryGroup(content.groups, content.primary_group_id);
    switch (content.groups.length) {
      case 0:
        return '';
      case 1:
        return primaryGroup?.title;
      default:
        if (content.primary_group_id) {
          const secondaryGroups = content.groups
            .filter(group => group.id != content.primary_group_id)
            .map(group => group?.title)
            .join(', ');

          return `${primaryGroup?.title} - ${t('posting:repostTo')}: ${secondaryGroups}`;
        }

        return `${primaryGroup?.title} +${content.groups?.length - 1}`;
    }
  }

  function handleAllCheckedChange(event: ChangeEvent<HTMLInputElement>) {
    setCheckedState(event.target.checked ? CheckedMode.PAGE : CheckedMode.OFF);
  }

  function handleCheckedChange(checkedId: string, checked: boolean) {
    let nextCheckedIds = [];

    if (checkedMode) {
      setCheckedState(CheckedMode.OFF);
      nextCheckedIds = results.map(item => item.content_id).filter(id => id !== checkedId);
    } else if (checked) {
      nextCheckedIds = checkedIds.slice();
      nextCheckedIds.push(checkedId);
    } else {
      nextCheckedIds = checkedIds.filter(id => id !== checkedId);
    }

    setCheckedState(nextCheckedIds);
  }

  function renderCheckbox(content: ContentFragment) {
    const primaryGroup = getPrimaryGroup(content.groups, content.primary_group_id);
    const is4EyeModeration =
      primaryGroup?.content_approval_type === GroupContentApprovalType.FourEyes;

    if (has4EyeModeration && is4EyeModeration) {
      return (
        <Tooltip title={t('content:fourEyesCheckboxTooltip')}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <IndeterminateCheckBoxOutlinedIcon sx={styles.disabled} />
          </Box>
        </Tooltip>
      );
    }

    return (
      <Checkbox
        name={content.content_id}
        checked={isAllChecked || checkedIds.includes(content.content_id)}
        onChange={event => handleCheckedChange(content.content_id, event.target.checked)}
        sx={styles.checkbox}
      />
    );
  }

  const columns: DataTableColumn<ContentFragment>[] = [
    {
      width: 48,
      headerCell: {
        padding: 'checkbox',
        render:
          filter.moderation === GroupContentApprovalType.FourEyes ? null : (
            <Checkbox
              checked={isAllChecked}
              onChange={handleAllCheckedChange}
              sx={styles.checkbox}
            />
          ),
      },
      bodyCell: {
        padding: 'checkbox',
        render: renderCheckbox,
      },
    },
    {
      width: IMAGE_SIZE + 5,
      headerCell: {
        padding: 'dense',
        render: t('admin:postPreview'),
      },
      bodyCell: {
        padding: 'none',
        render: content => <PostImage rounded size={IMAGE_SIZE} content={content} />,
      },
    },
    {
      width: 400,
      headerCell: {
        render: '',
      },
      bodyCell: {
        render: content => {
          return isUrl(content) ? (
            <UrlMetadata content={content} color={theme.palette.grey[600]} />
          ) : (
            <Body content={content} color={theme.palette.grey[600]} />
          );
        },
      },
    },
    {
      width: 90,
      headerCell: {
        render: t('common:type'),
      },
      bodyCell: {
        render: content => (
          <Typography title={getType(content)} noWrap>
            {getType(content)}
          </Typography>
        ),
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('admin:submitter'),
      },
      bodyCell: {
        render: content => {
          const submitter = getSubmitter(content);
          const submitterText = getSubmitterText(content);

          return (
            <Typography noWrap title={submitterText}>
              {submitter}
            </Typography>
          );
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:role'),
      },
      bodyCell: {
        render: content => (
          <TextTransform transform="capitalize">
            <Typography title={content.submitter_role}>{content.submitter_role}</Typography>
          </TextTransform>
        ),
      },
    },
    {
      width: 140,
      headerCell: {
        render: t('common:group_plural'),
      },
      bodyCell: {
        render: content => {
          const titlesEl = getGroupTitles(content);
          const titleText = getGroupTitlesText(content);

          return (
            <Typography noWrap title={titleText}>
              {titlesEl}
            </Typography>
          );
        },
      },
    },
    {
      width: 140,
      headerCell: {
        render: t('groups:moderation'),
      },
      bodyCell: {
        render: ({ user_id, audit, groups, primary_group_id }) => {
          const isCreator = user_id === currentUser.id;
          const createdByAdminOrModerator =
            audit.find(aud => aud.ACTION === 'four_eyes_created') || ({} as ContentAudit);
          const lastAudit = audit[audit.length - 1] || ({} as ContentAudit);
          const approvalCount = Number(lastAudit.message) || 0;
          const isApprovedByYou = approvalCount === 1 && lastAudit?.by_user_id === currentUser.id;
          const primaryGroup = getPrimaryGroup(groups, primary_group_id);
          const is4EyesModeration =
            primaryGroup?.content_approval_type === GroupContentApprovalType.FourEyes;
          const size = !isEmpty(createdByAdminOrModerator) ? 1 : 2;

          if (is4EyesModeration) {
            return (
              <Box display="flex">
                <Typography marginRight={0.5} sx={(isCreator || isApprovedByYou) && styles.icon}>
                  {t('components:indexOf', { index: approvalCount, size })}
                </Typography>
                {isCreator ? (
                  <LockOutlinedIcon sx={styles.icon} fontSize="small" />
                ) : isApprovedByYou ? (
                  <CheckCircleRoundedIcon sx={styles.icon} fontSize="small" />
                ) : (
                  <ErrorOutlineRoundedIcon fontSize="small" />
                )}
              </Box>
            );
          }

          return (
            <Box display="flex">
              <Typography marginRight={0.5}>
                {t('components:indexOf', { index: approvalCount, size: 1 })}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      width: 200,
      headerCell: {
        sort: {
          field: SearchContentSortFields.CreatedAt,
          direction: SortDirections.Desc,
        },
        render: t('common:date'),
      },
      bodyCell: {
        render: content => {
          const mom = moment.unix(content.publish_at);
          const format = `${mom.format('l')} - ${mom.format('h:mm A')}`;
          return (
            <Typography noWrap title={format}>
              {format}
            </Typography>
          );
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:branded'),
      },
      bodyCell: {
        render: content => {
          return (
            content.is_branded && (
              <Grid container justifyContent="center">
                <Grid>
                  <CheckRoundedIcon />
                </Grid>
              </Grid>
            )
          );
        },
      },
    },
  ];

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  function getGroupIds(content: ContentFragment) {
    const data = results?.filter(result => result.content_id === content.content_id);
    return flatten(data.map(datum => datum.group_ids));
  }

  const actions: DataTableActions<ContentFragment> = content => {
    const groupIds = getGroupIds(content);

    const contentGroupList = groupIds.map(groupId => ({
      group_id: groupId,
      content_id: content.content_id,
    })) as ContentGroupInput[];

    return (
      <Actions
        variables={{
          bulk_update: {
            approval,
            content_group_list: contentGroupList,
          },
        }}
        count={1}
        contents={results}
        updateContentMutationOptions={updateContentMutationOptions}
      />
    );
  };

  function getColumns() {
    const placeholderColumn = {
      width: 48,
      headerCell: {
        render: <div />,
      },
      bodyCell: {
        render: () => <div />,
      },
    };

    if (!has4EyeModeration || !showCheckbox) {
      columns.splice(7, 1);
    }

    if (!showCheckbox) {
      const withoutCheckbox = columns.slice(1);
      return [placeholderColumn, ...withoutCheckbox];
    }

    return columns;
  }

  return (
    <DataTable
      data={results}
      columns={getColumns()}
      pagination={pagination}
      actions={actions}
      sortBy={sortBy}
      onSort={onSort}
      sx={styles.tableBodyRow}
      rowSelection={
        <DataTableRowSelection
          noun={t('common:post_plural').toLowerCase()}
          count={actionCount}
          total={count}
          mode={checkedMode}
          onSelectAll={setCheckedState}
          disableMatchAll
        />
      }
    />
  );
};

export default PostManagementTable;
